import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/* FastAPI 422 returns detail as an array of {msg,...} objects — flatten before rendering. */
function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const GoogleIcon = () => (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

const Login = () => {
    const [mode, setMode] = useState("login");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            if (mode === "login") {
                await login(email, password);
            } else {
                await register(name, email, password);
            }
            navigate("/");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const onGoogleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <main className="py-20 md:py-28" data-testid="login-page">
            <div className="wrap flex justify-center">
                <div className="w-full max-w-md">
                    <p className="eyebrow">Members</p>
                    <h2 className="mt-4 text-3xl tracking-tight sm:text-4xl">
                        {mode === "login" ? (
                            <>
                                Sign in to <em className="italic text-brass">the room.</em>
                            </>
                        ) : (
                            <>
                                Create your <em className="italic text-brass">account.</em>
                            </>
                        )}
                    </h2>

                    <form
                        onSubmit={onSubmit}
                        data-testid="login-form"
                        className="mt-10 border border-line bg-surface p-8 sm:p-10"
                    >
                        {mode === "register" && (
                            <div className="mb-6">
                                <label htmlFor="auth-name" className="field-label">
                                    Full name
                                </label>
                                <input
                                    id="auth-name"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    data-testid="login-name-input"
                                    className="field"
                                    placeholder="Your name"
                                />
                            </div>
                        )}

                        <div className="mb-6">
                            <label htmlFor="auth-email" className="field-label">
                                Email
                            </label>
                            <input
                                id="auth-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="field"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div className="mb-2">
                            <label htmlFor="auth-password" className="field-label">
                                Password
                            </label>
                            <input
                                id="auth-password"
                                type="password"
                                required
                                minLength={mode === "register" ? 8 : undefined}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="field"
                                placeholder={
                                    mode === "register"
                                        ? "At least 8 characters"
                                        : "Your password"
                                }
                            />
                        </div>

                        {error && (
                            <p
                                className="mt-4 text-sm leading-relaxed text-red-700 dark:text-red-400"
                                data-testid="login-error"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit-btn"
                            className="btn-primary mt-8 w-full disabled:opacity-60"
                        >
                            {busy
                                ? "One moment…"
                                : mode === "login"
                                ? "Sign in"
                                : "Create account"}
                        </button>

                        <div className="my-7 flex items-center gap-4">
                            <span className="h-px flex-1 bg-line" />
                            <span className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                                or
                            </span>
                            <span className="h-px flex-1 bg-line" />
                        </div>

                        <button
                            type="button"
                            onClick={onGoogleSignIn}
                            data-testid="google-signin-btn"
                            className="btn-ghost w-full"
                        >
                            <GoogleIcon />
                            Continue with Google
                        </button>

                        <p className="mt-7 text-center text-sm text-ink-muted">
                            {mode === "login"
                                ? "New to The Panel Room?"
                                : "Already have an account?"}{" "}
                            <button
                                type="button"
                                data-testid="auth-mode-toggle"
                                onClick={() => {
                                    setMode(mode === "login" ? "register" : "login");
                                    setError("");
                                }}
                                className="text-brass underline-offset-4 transition-colors duration-300 hover:underline"
                            >
                                {mode === "login"
                                    ? "Create an account"
                                    : "Sign in instead"}
                            </button>
                        </p>
                    </form>

                    <p className="mt-6 text-center text-xs text-ink-muted">
                        Sessions are protected with secure cookies — no passwords are
                        ever stored in plain text.
                    </p>
                </div>
            </div>
        </main>
    );
};

export default Login;
